import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { debounce } from 'lodash';

import AppButton from '../Components/appButton';
import AppHeader from '../Components/appHeader';
import AudioPlayer from '../Components/audioPlayer';
import BottomSlide from '../Components/bottomSlide';
import ErrorMessage from '../Components/errorMessage';
import HomeButton from '../Components/homeButton';
import List from '../Components/list';
import MainScanTile4 from '../Components/mainScanTile4';
import MainScanTile4WithDate from '../Components/mainScanTile4WithDate';
import PickLocationsTile from '../Components/PicklocationsTile';
import ScanScreenSubHeader from '../Components/ScanScreenSubHeader';
import ScanTwoFields from '../Components/ScanTwoFields';
import Section from '../Components/section';
import SuccessMessage from '../Components/successMessage';
import WarningMessage from '../Components/warningMessage';
import { hideKeyboard } from '../utility/hideKeyboard';

import * as actions from "../Redux/mainRedux/mainActions";
import { slice } from '../Redux/mainRedux/mainSlice';

const ScanScreenType4 = () => {
  // Redux state
  const {
    baseAPIURL,
    token,
    sessionStorage,
    scan4,
    error,
    warning,
    success,
    progressiveArray
  } = useSelector((state) => state.slice);

  // Redux dispatcher
  const dispatch = useDispatch();

  // Local state
  const [showBackButton, setShowBackButton] = useState(false);
  const [extDescription, setExtDescription] = useState(null);
  const [showSearch, setShowSearch] = useState(false);
  const [hideArea, setHideArea] = useState(false);
  const [showMiniExitPopup, setShowMiniExitPopup] = useState(false);
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [errorMessageData, setErrorMessageData] = useState(null);
  const [checkSuccessBeep, setSuccessBeep] = useState(false);
  const [scanError, setScanError] = useState(false);
  const [errorOpen, setErrorOpen] = useState(false);
  const [showWarningMessage, setShowWarningMessage] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [scanValue, setScanValue] = useState("");
  const [customReferenceFields, setCustomReferenceFields] = useState({
    customReference1: "",
    customReference2: ""
  });
  const [dateReceived, setDateReceived] = useState(
    new Intl.DateTimeFormat(['en-US', 'id'], { year: 'numeric', day: '2-digit', month: '2-digit' }).format(new Date())
  );
  const [comments, setComments] = useState("");

  // Beep Sounds
  let soundGoodScan = '/Assets/audio/goodscan.mp3';
  let soundBadScan = '/Assets/audio/badscan.mp3';

  const inputRef = useRef();

  // Handle back button click
  const handleBackClick = () => {
    // Cancel any active API requests
    window.source.cancel('Operation cancelled by the user.');

    // If back click was from the search screen then just
    // close the search screen
    if (showSearch) {
      setShowSearch(false);
      setShowBackButton(false);
      return;
    }

    // Execute the back button request if one was defined
    if (scan4?.back_button) {
      let body = {
        apiSessionStorage: sessionStorage,
        identifier: scan4?.back_button?.identifier,
        ...scan4?.back_button?.call_back_url_params
      };
      dispatch(actions.generic_call(
        scan4?.back_button?.base_url,
        scan4?.back_button?.callback_url,
        body,
        'post',
        token
      ));
      setScanValue("");
    }
  };

  // Handle changing scan value
  const handleChangeScanValue = (e) => {
    const newValue = (typeof e === 'string') ? e : e?.target?.value;
    setScanValue(newValue);
    debounceProgressiveSearch(newValue);
  };

  // Perform progressive search
  const handleProgressiveSearch = (searchValue) => {
    if (scan4?.progessive_search && searchValue) {
      let body = {
        apiSessionStorage: sessionStorage,
        ...scan4?.progessive_search?.call_back_url_params,
        scan_value: searchValue,
        keyByField: scan4?.progessive_search?.key,
      };
      dispatch(
        actions.checkProgressiveSearch(
          baseAPIURL,
          scan4?.progessive_search?.callback_url,
          body,
          "post",
          token
        )
      );
      hideKeyboard();
    } else {
      dispatch(slice.actions.setProgressiveArray(null));
    }
  }

  // Debounce progressive search
  const debounceProgressiveSearch = useCallback(
    debounce((searchValue) => {
      handleProgressiveSearch(searchValue);
    }, 500)
  , [baseAPIURL, scan4?.progessive_search, sessionStorage, token]);

  // Stop debounced function after unmounting
  useEffect(() => {
    return () => {
      debounceProgressiveSearch.cancel();
    }
  }, [debounceProgressiveSearch]);

  // Handle progressive search item click
  const handleProgressiveItemClick = (item) => {
    let itemValue = item?.value || item[scan4.progessive_search.key] || "";
    setScanValue(itemValue);
    dispatch(slice.actions.setProgressiveArray(null));
    handleSubmit(itemValue);
  };

  // Handle submit of scan value
  const handleSubmit = (sValue) => {
    // Get the scan value from argument or from state
    let submitValue = sValue ? sValue : scanValue;
    if (typeof submitValue === 'string') {
      submitValue = submitValue.trim();
    }
    // Validate empty scan
    if (!scan4?.empty_scan && !submitValue) {
      setErrorMessageData({
        detail: scan4?.scan_error_message
          ? scan4?.scan_error_message
          : 'Barcode not recognized. Please scan again properly.'
      });
      setShowErrorMessage(true);
      return false;
    }

    // Submit the request
    if (scan4?.callback_url) {
      let body = {
        apiSessionStorage: sessionStorage,
        ...scan4?.call_back_url_params,
        scan_value: submitValue
      };
      dispatch(actions.generic_call(
        baseAPIURL,
        scan4?.callback_url,
        body,
        'post',
        token
      ));
      setScanValue("");
      return true;
    }
  };

  // Handle button submit
  const handleButtonSubmit = (data) => {
    if (data.callback_url) {
      let body = {
        apiSessionStorage: sessionStorage,
        ...data?.call_back_url_params,
        scan_value: scanValue
      };
      if (scan4?.date_screen) {
        let currentDate = new Intl.DateTimeFormat(["ban", "id"], {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
        }).format(new Date());
        body = {
          ...body,
          dateReceived: dateReceived || currentDate,
          comments: comments
        }
      } else {
        // Custom reference screen assummed
        body = {
          ...body,
          ...customReferenceFields
        };
      }
      dispatch(actions.generic_call(
        baseAPIURL,
        data?.callback_url,
        body,
        'post',
        token
      ));
      setScanValue("");
      setCustomReferenceFields({
        customReference1: "",
        customReference2: ""    
      });
    }
  };

  // Handle mini exit
  const handleMiniExit = () => {
    // Close the mini exit popup
    setShowMiniExitPopup(false);

    // Execute the mini exit request if one was defined
    if (scan4?.mini_exit_btn?.btn_option[0]?.on_click_url) {
      let body = {
        apiSessionStorage: sessionStorage,
        ...scan4?.mini_exit_btn?.btn_option[0]?.on_click_url_params
      };
      dispatch(actions.generic_call(
        baseAPIURL,
        scan4?.mini_exit_btn?.btn_option[0]?.on_click_url,
        body,
        'post',
        token
      ));
    }
  };

  // Handle error message close
  const handleErrorMessageClose = () => {
    // Close & clear the error message
    setShowErrorMessage(false);
    setErrorMessageData(null);

    // Redirect after error
    if (error?.call_back_url && error?.call_back_url_params) {
      let body = {
        apiSessionStorage: sessionStorage,
        ...error.call_back_url_params
      };
      dispatch(actions.generic_call(
        baseAPIURL,
        error.call_back_url,
        body,
        'post',
        token
      ));
    }

    // Clear error
    dispatch(slice.actions.clearError());
  };

  // Handle redirect after success
  const handleRedirectAfterSuccess = () => {
    // Close the success message
    setShowSuccessMessage(false);

    // Redirect after success
    if (success?.call_back_url) {
      let body = {
        apiSessionStorage: sessionStorage,
        ...success.call_back_url_params
      };
      dispatch(actions.generic_call(
        success.base_url,
        success.call_back_url,
        body,
        'post',
        token
      ));
    }

    // Clear success
    dispatch(slice.actions.clearSuccess());
  };

  // Handle open search
  const handleOpenSearch = () => {
    setShowSearch(true);
    setShowBackButton(true);
  };

  // Handle search item click
  const handleSearchItemClick = (item) => {
    setShowSearch(false);
    let itemValue = item?.value || item[scan4.progessive_search.key] || "";
    setScanValue(itemValue);
    handleSubmit(itemValue);
  };

  // Show ext description
  const showExtDescription = (text) => {
    if (typeof text === 'string' && text.length > 24) {
      setExtDescription(text);
    }
  };

  // @todo Not sure how these functions are being called...
  window.scrollEnable = function () {
    setHideArea(true);
  };

  window.scrollDisable = function () {
    setHideArea(false);
  };

  // Handle error, warning, and success
  useEffect(() => {
    // error
    if (error) {
      setErrorMessageData(error);
      setShowErrorMessage(true);
      if (error.code === 306) {
        localStorage.clear();
        setTimeout(() => {
          setShowErrorMessage(false);
          dispatch(slice.actions.clearError());
          dispatch(slice.actions.setPage('login'));
        }, 2000);
      }
    } else {
      setShowErrorMessage(false);
      setErrorMessageData(null);
    }

    // warning
    if (warning) {
      setShowWarningMessage(true);
      setTimeout(() => {
        setShowWarningMessage(false);
        dispatch(slice.actions.clearWarning());
      }, 2000);
    } else {
      setShowWarningMessage(false);
    }

    // success
    if (success) {
      setShowSuccessMessage(true);
    } else {
      setShowSuccessMessage(false);
    }
  }, [error, warning, success]);

  const scanHandler = () => {
    // Get the scan value from argument or from state
    let submitValue = scanValue;
    if (typeof submitValue === 'string') {
      submitValue = submitValue.trim();
    }
    // Validate empty scan
    if (!scan4?.empty_scan && !submitValue) {
      setErrorMessageData({
        detail: scan4?.scan_error_message
          ? scan4?.scan_error_message
          : 'Barcode not recognized. Please scan again properly.'
      });
      setShowErrorMessage(true);
      return false;
    }

    // Submit the request
    if (scan4?.callback_url) {
      let body = {
        apiSessionStorage: sessionStorage,
        ...scan4?.call_back_url_params,
        scan_value: submitValue
      };
      dispatch(actions.generic_call(
        baseAPIURL,
        scan4?.callback_url,
        body,
        'post',
        token
      ));
      setScanValue("");
      return true;
    }
  };

  return (
    <>
      <AppHeader
        menuIconVisible={false}
        backIconVisible={scan4?.back_button ? true : showBackButton}
        onBackClick={handleBackClick}
        textVisible={true}
        text={scan4?.title}
        warehouseName={scan4?.warehouse_selector_button_text}
        warehouseEnable={scan4?.warehouse_list?.length > 0 ? true : false}
        waveDesc={scan4?.description}
        onHeaderDescClick={showExtDescription}
      />
      
      {!scan4?.sub_header && (
        <ScanScreenSubHeader
          title1={scan4?.item?.title}
          value1={scan4?.item?.value}
          title2={scan4?.pallet?.title}
          value2={scan4?.pallet?.value}
          title3={scan4?.lot_title ? scan4?.lot_title?.title : null}
          value3={scan4?.lot_title ? scan4?.lot_title?.value : null}
          title4={scan4?.tag ? scan4?.tag?.title : null}
          value4={scan4?.tag ? scan4?.tag?.value : null}
          title5={scan4?.expiry_date ? scan4?.expiry_date?.title : null}
          value5={scan4?.expiry_date ? scan4?.expiry_date?.value : null}
          title6={scan4?.serial_title ? scan4?.serial_title?.title : null}
          value6={scan4?.serial_title ? scan4?.serial_title?.value : null}
          title7={scan4?.qty_title ? scan4?.qty_title?.title : null}
          value7={scan4?.qty_title ? scan4?.qty_title?.value : null}
          remainValue={{ total: scan4?.qty_total, remaining: scan4?.qty_remaining }}
          remainText={scan4?.remaining_text}
          onSubHeaderClick={showExtDescription}
          gridLayout={scan4?.gridLayout}
        />
      )}

      <div className="cpt-10 cpl-15 cpr-15 cpb-15 backgroundColor-Type5">
        <div className="wfScreen">
          <div className="row">
            {!showSearch && (
              <>
                {scan4?.upper_left && (
                  <div className="col-sm-6 col-xs-6 col-6 cpr-5">
                    <PickLocationsTile
                      text={scan4?.upper_left?.title}
                      value={scan4?.upper_left?.value}
                      bordercolor={scan4?.upper_left?.border_color}
                      bgcolor={scan4?.upper_left?.bg_color}
                    />
                  </div>
                )}
                {scan4?.upper_right && (
                  <div className="col-sm-6 col-xs-6 col-6 cpl-2">
                    <PickLocationsTile
                      text={scan4?.upper_right?.title}
                      value={scan4?.upper_right?.value}
                      bordercolor={scan4?.upper_right?.border_color}
                      bgcolor={scan4?.upper_right?.bg_color}
                    />
                  </div>
                )}
                {scan4?.upper_left_bottom && (
                  <div className="col-sm-6 col-xs-6 col-6 cpr-5 cpt-5">
                    <PickLocationsTile
                      text={scan4?.upper_left_bottom?.title}
                      value={scan4?.upper_left_bottom?.value}
                    />
                  </div>
                )}
                {scan4?.upper_right_bottom && (
                  <div className="col-sm-6 col-xs-6 col-6 cpl-2 cpt-5">
                    <PickLocationsTile
                      text={scan4?.upper_right_bottom?.title}
                      value={scan4?.upper_right_bottom?.value}
                    />
                  </div>
                )}
              </>
            )}

            {!showSearch && !scan4?.date_screen && (
              <div className="col-sm-12 col-xs-12 col-12">
                {!scan4?.custom_fields ? (
                  <MainScanTile4
                    blueTitle={scan4?.blue_title}
                    unboundedText={scan4?.blue_text}
                    unboundedText1={scan4?.blue_text1}
                    boundedText={scan4?.blue_dotted_text}
                    button_type={scan4?.button_type}
                    InputPlaceholder={scan4?.scan_text}
                    numericKeyboard={scan4?.numeric_keyboard}
                    scanValue={scanValue}
                    onChange={handleChangeScanValue}
                    onSubmit={handleSubmit}
                    onOpenSearch={handleOpenSearch}
                    searchoptions={scan4?.search_object}
                    scanicon={scan4?.scan_icon}
                    scanScreen={scan4}
                    onProgressiveItemClick={handleProgressiveItemClick}
                    onScanHandler={scanHandler}
                  />
                ) : (
                  <ScanTwoFields
                    blueTitle={scan4?.blue_title}
                    unboundedText={scan4?.blue_text}
                    unboundedText1={scan4?.blue_text1}
                    boundedText={scan4?.blue_dotted_text}
                    button_type={scan4?.button_type}
                    Input1Placeholder={scan4?.scan_text}
                    Input2Placeholder={scan4?.scan_text_second}
                    numericKeyboard={scan4?.numeric_keyboard}
                    customReferenceFields={customReferenceFields}
                    setCustomReferenceFields={setCustomReferenceFields}
                  />
                )}
              </div>
            )}

            {scan4?.framed_footer_list && (
              <div className="col-sm-12 col-xs-12 col-12 mt-2">
                <Section framed_footer={scan4?.framed_footer_list} />
              </div>
            )}

            {showSearch && !scan4?.date_screen && (
              <List
                titles={scan4?.search_object.title}
                initialCollection={scan4?.search_object?.value}
                searchLoading={scan4?.search_loading}
                isSearchable={true}
                onRowClick={handleSearchItemClick}
              />
            )}

            {scan4?.date_screen && (
              <div className="col-sm-12 col-xs-12 col-12">
                <MainScanTile4WithDate
                  unboundedText={scan4?.blue_text}
                  DatePlaceholder={"Select Date"}
                  CommentsPlaceholder={scan4?.scan_text}
                  dateReceived={dateReceived}
                  setDateReceived={setDateReceived}
                  comments={comments}
                  setComments={setComments}
                  onSubmit={handleButtonSubmit}
                />
              </div>
            )}

            {(success?.beep_code === 200 ||
              error?.code === 409) && (
              <AudioPlayer
                sound={
                  success?.beep_code === 200
                    ? soundGoodScan
                    : success?.beep_code === 409
                    ? soundBadScan
                    : error?.code === 409
                    ? soundBadScan
                    : ""
                }
                loop={false}
                muted={false}
              />
            )}
          </div>
          {scan4?.mini_exit_btn && hideArea && (
            <div className="d-flex col-sm-6 col-xs-6 cpl-2">
              <div
                className={"ExitTile backgroundColor-Type3 font-small"}
                onClick={() => setShowMiniExitPopup(true)}
              >
                <span className="font-medium textColor-Type8">
                  {scan4?.mini_exit_btn?.btn_text}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      <BottomSlide
        open={showMiniExitPopup}
        closeHandle={() => setShowMiniExitPopup(false)}
      >
        <div className="w-100">
          <h5 className="text-center my-4">
            {scan4?.mini_exit_btn?.blue_text}
          </h5>
          <div className="d-flex gap-2 p-2">
            <AppButton
              displayText={scan4?.mini_exit_btn?.btn_option[0].title}
              onClick={handleMiniExit}
            />
            <AppButton
              displayText={scan4?.mini_exit_btn?.btn_option[1].title}
              onClick={() => setShowMiniExitPopup(false)}
              isOutlined={true}
            />
          </div>
        </div>
      </BottomSlide>

      <BottomSlide
        children={extDescription}
        open={extDescription !== null}
        closeHandle={() => setExtDescription(null)}
      />

      <div className="cml-30 cmr-30 col-sm-12 col-xs-12 fixed-bottom index cmb-30">
        {/* Will show once button response recieve from backend */}
        <div className="px-3">
          {!showSearch && scan4?.complete_button && !hideArea && (
            <AppButton
              displayText={scan4?.complete_button?.button_text}
              isOutlined={scan4?.complete_button?.isOutlined}
              onClick={() => handleButtonSubmit(scan4?.complete_button)}
            />
          )}
          {scan4?.next_button && !hideArea && (
            <AppButton
              displayText={scan4?.next_button?.button_text}
              isOutlined={false}
              onClick={() => handleButtonSubmit(scan4?.next_button)}
            />
          )}
        </div>
      </div>

      {scan4?.allow_home_button && !showBackButton && !hideArea && (
        <HomeButton redirectData={scan4?.allow_home_button} />
      )}

      {showErrorMessage && (
        <ErrorMessage
          open={showErrorMessage}
          onClose={handleErrorMessageClose}
          error={errorMessageData}
        />
      )}

      {showWarningMessage && <WarningMessage open={showWarningMessage} warning={warning} />}

      {showSuccessMessage && (
        <SuccessMessage
          open={showSuccessMessage}
          message={success.popup_message}
          redirectAfterSuccess={handleRedirectAfterSuccess}
          icon={success.popup_icon}
        />
      )}
    </>
  );
};

export default ScanScreenType4;
